import { useCallback, useEffect, useState } from "react";

import { api, ApiFailure } from "./client";

// Toda vista de datos repite el mismo ciclo: cargar, distinguir los cuatro
// estados que exige CLAUDE.md §6 y recargar después de una mutación. Este hook
// lo escribe una vez.
//
// La recarga silenciosa existe por una razón concreta: cuando alguien acaba de
// crear algo, o cuando una tabla se refresca sola, vaciar la pantalla para
// volver a llenarla hace parpadear lo que la persona está leyendo. En modo
// silencioso los datos anteriores se quedan y el error se reporta aparte, como
// aviso en lugar de como reemplazo.

export type Resource<T> = {
  data: T | null;
  error: string | null;
  /** `data === null && error === null` significa "cargando". */
  loading: boolean;
  reload: (silent?: boolean) => Promise<void>;
};

/**
 * useResource carga un recurso de la API.
 *
 * `path` acepta `null` para los recursos que solo se piden bajo condición (por
 * ejemplo las troncales, que son de plataforma): así la vista no tiene que
 * montar el hook condicionalmente, que React no permite.
 */
export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(
    async (silent = false) => {
      if (path === null) return;
      if (!silent) {
        setData(null);
        setError(null);
      }
      try {
        setData(await api.get<T>(path));
        setError(null);
      } catch (err) {
        setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
      }
    },
    [path],
  );

  useEffect(() => {
    void reload(false);
  }, [reload]);

  return { data, error, loading: data === null && error === null, reload };
}

/** Mensaje de un fallo de la API, sin filtrar detalles internos. */
export function failureMessage(err: unknown, fallback: string): string {
  return err instanceof ApiFailure ? err.error.message : fallback;
}
